import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LogOut, ShoppingCart, Trash2, Plus, Minus, Search, Ban } from 'lucide-react';
import { styles, Modal } from '../theme';
import { API_URL, fetchWithAuth, filterToUsersOwnBranches } from '../api';
import { usePosOperator } from '../hooks/usePosOperator';
import PosPinPad from './PosPinPad';
import PosReceiptView from './PosReceiptView';
import { formatCurrency } from '../utils/formatUtils';
import { getPHTDate } from '../utils/dateUtils';

const formatPeso = (v) => `₱${formatCurrency(v)}`;
const formatTime = (v) => {
  if (!v) return '-';
  const d = getPHTDate(v);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const PosSales = ({ token, onGoToShifts, setSidebarOpen }) => {
  const { operator, operatorToken, setOperator, clearOperator } = usePosOperator();

  // The checkout screen needs all the width it can get on a phone — collapse
  // the sidebar while it's active, restore it on the way out.
  useEffect(() => {
    if (!setSidebarOpen) return;
    setSidebarOpen(false);
    return () => setSidebarOpen(true);
  }, [setSidebarOpen]);

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [currentShift, setCurrentShift] = useState(null);
  const [checkingShift, setCheckingShift] = useState(false);

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');

  const [cart, setCart] = useState([]);
  const [addingItem, setAddingItem] = useState(null);
  const [addQty, setAddQty] = useState('1');
  const [addPrice, setAddPrice] = useState('');

  const [showCart, setShowCart] = useState(false);
  const [saleDiscountId, setSaleDiscountId] = useState('');
  const [saleDiscountAmount, setSaleDiscountAmount] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [amountTendered, setAmountTendered] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [receiptSale, setReceiptSale] = useState(null);
  const [todaySales, setTodaySales] = useState([]);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-POS-Operator-Token': operatorToken };

  useEffect(() => {
    fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        const owned = filterToUsersOwnBranches(Array.isArray(data) ? data : []);
        setBranches(owned);
        if (owned.length > 0) setBranchId(String(owned[0].id));
      })
      .catch(err => console.error('Error fetching branches:', err));
  }, [token]);

  const fetchCurrentShift = useCallback(async () => {
    if (!branchId) return;
    setCheckingShift(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/shifts/current?branch_id=${branchId}`, { headers: { Authorization: `Bearer ${token}` } });
      setCurrentShift(res.status === 404 ? null : await res.json());
    } catch (err) {
      console.error('Error checking shift:', err);
    } finally {
      setCheckingShift(false);
    }
  }, [token, branchId]);

  const fetchCatalog = useCallback(async () => {
    try {
      const [catRes, itemRes, discRes, pmRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/pos/categories/all`, { headers: { Authorization: `Bearer ${token}` } }),
        fetchWithAuth(`${API_URL}/pos/items/all`, { headers: { Authorization: `Bearer ${token}` } }),
        fetchWithAuth(`${API_URL}/pos/discounts/all`, { headers: { Authorization: `Bearer ${token}` } }),
        fetchWithAuth(`${API_URL}/pos/payment-methods/all`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const asArray = (v) => Array.isArray(v) ? v : [];
      setCategories(asArray(await catRes.json()));
      setItems(asArray(await itemRes.json()));
      setDiscounts(asArray(await discRes.json()));
      const pmData = asArray(await pmRes.json());
      setPaymentMethods(pmData);
      setPaymentMethod(prev => prev || (pmData[0] ? String(pmData[0].id) : ''));
    } catch (err) {
      console.error('Error fetching POS catalog:', err);
    }
  }, [token]);

  const fetchTodaySales = useCallback(async () => {
    if (!branchId) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/sales?branch_id=${branchId}&per_page=10`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setTodaySales(data.data || []);
    } catch (err) {
      console.error('Error fetching today\'s sales:', err);
    }
  }, [token, branchId]);

  useEffect(() => { fetchCurrentShift(); fetchTodaySales(); }, [fetchCurrentShift, fetchTodaySales]);
  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const filteredItems = useMemo(() => {
    let list = items;
    if (activeCategory !== 'all') {
      list = list.filter(i => String(i.pos_category_id) === String(activeCategory));
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i => i.item_name.toLowerCase().includes(q) || (i.sku || '').toLowerCase().includes(q));
    }
    return list;
  }, [items, activeCategory, search]);

  const cartSubtotal = useMemo(() => cart.reduce((sum, l) => sum + l.quantity * l.unit_price, 0), [cart]);

  const selectedSaleDiscount = discounts.find(d => String(d.id) === String(saleDiscountId));
  const computedSaleDiscount = useMemo(() => {
    if (!selectedSaleDiscount) return 0;
    if (selectedSaleDiscount.value === null) return parseFloat(saleDiscountAmount) || 0;
    return selectedSaleDiscount.discount_type === 'percentage'
      ? cartSubtotal * (parseFloat(selectedSaleDiscount.value) / 100)
      : parseFloat(selectedSaleDiscount.value);
  }, [selectedSaleDiscount, saleDiscountAmount, cartSubtotal]);

  const grandTotal = Math.max(cartSubtotal - computedSaleDiscount, 0);
  const selectedPaymentMethod = paymentMethods.find(m => String(m.id) === String(paymentMethod));
  const changeAmount = selectedPaymentMethod?.is_cash ? (parseFloat(amountTendered) || 0) - grandTotal : null;

  const openAddModal = (item) => {
    setAddingItem(item);
    setAddQty('1');
    setAddPrice(item.pos_price === null ? '' : String(item.pos_price));
  };

  const confirmAddToCart = () => {
    const qty = parseFloat(addQty);
    const price = parseFloat(addPrice);
    if (!qty || qty <= 0) { alert('Enter a valid quantity'); return; }
    if (isNaN(price) || price < 0) { alert('Enter a valid price'); return; }

    setCart(prev => {
      const existingIdx = prev.findIndex(l => l.item_id === addingItem.id && l.unit_price === price);
      if (existingIdx >= 0 && addingItem.sold_by === 'each') {
        const next = [...prev];
        next[existingIdx] = { ...next[existingIdx], quantity: next[existingIdx].quantity + qty };
        return next;
      }
      return [...prev, {
        item_id: addingItem.id, item_name: addingItem.item_name, sold_by: addingItem.sold_by,
        quantity: qty, unit_price: price
      }];
    });
    setAddingItem(null);
  };

  const updateCartQty = (idx, delta) => {
    setCart(prev => prev.map((l, i) => i === idx ? { ...l, quantity: Math.max(l.quantity + delta, 0.001) } : l));
  };

  const removeCartLine = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));

  const resetSaleForm = () => {
    setCart([]);
    setSaleDiscountId('');
    setSaleDiscountAmount('0');
    setPaymentMethod(paymentMethods[0] ? String(paymentMethods[0].id) : '');
    setAmountTendered('');
    setShowCart(false);
  };

  const handleCompleteSale = async () => {
    if (cart.length === 0) { alert('Cart is empty'); return; }
    if (!paymentMethod) { alert('Select a payment method'); return; }
    if (selectedPaymentMethod?.is_cash && (parseFloat(amountTendered) || 0) < grandTotal) {
      alert('Amount tendered is less than the total due');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/sales`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          shift_id: currentShift.id,
          branch_id: branchId,
          payment_method: paymentMethod,
          amount_tendered: selectedPaymentMethod?.is_cash ? amountTendered : null,
          sale_discount_id: saleDiscountId || null,
          sale_discount_amount: computedSaleDiscount,
          items: cart.map(l => ({ item_id: l.item_id, quantity: l.quantity, unit_price: l.unit_price }))
        })
      });
      const data = await res.json();
      if (res.ok) {
        resetSaleForm();
        fetchTodaySales();
        const detailRes = await fetchWithAuth(`${API_URL}/pos/sales/${data.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setReceiptSale(await detailRes.json());
      } else {
        alert('Error: ' + (data.error || JSON.stringify(data.error)));
      }
    } catch (err) {
      console.error('Error completing sale:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const viewReceipt = async (saleId) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/sales/${saleId}`, { headers: { Authorization: `Bearer ${token}` } });
      setReceiptSale(await res.json());
    } catch (err) {
      console.error('Error loading receipt:', err);
    }
  };

  const handleVoid = async (saleId) => {
    const reason = window.prompt('Reason for voiding this sale:');
    if (!reason) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/sales/${saleId}/void`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason })
      });
      if (res.ok) {
        fetchTodaySales();
      } else {
        const err = await res.json();
        alert('Error: ' + (err.error || JSON.stringify(err.error)));
      }
    } catch (err) {
      console.error('Error voiding sale:', err);
    }
  };

  if (!operator) {
    return <PosPinPad token={token} branchId={branchId} onVerified={(op, opToken) => setOperator(op, opToken)} />;
  }

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>POS Sales</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} style={{ ...styles.input, width: 'auto' }}>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button onClick={clearOperator} style={{ ...styles.button, ...styles.buttonSecondary }}>
            <LogOut size={16} /> {operator.fullname || operator.username}
          </button>
        </div>
      </div>

      {checkingShift ? (
        <div style={{ ...styles.card, textAlign: 'center', color: '#6b7280' }}>Checking shift status...</div>
      ) : !currentShift ? (
        <div style={{ ...styles.card, textAlign: 'center', padding: '40px' }}>
          <p style={{ marginBottom: '16px', color: '#6b7280' }}>No shift is open for this branch. Open one before ringing up sales.</p>
          {onGoToShifts && (
            <button onClick={onGoToShifts} style={styles.button}>Go to Shifts</button>
          )}
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                placeholder="Search items or scan SKU..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ ...styles.input, paddingLeft: '36px' }}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px', WebkitOverflowScrolling: 'touch' }}>
              <button
                onClick={() => setActiveCategory('all')}
                style={{
                  padding: '8px 16px', borderRadius: '20px', border: '1px solid #d1d5db', whiteSpace: 'nowrap',
                  backgroundColor: activeCategory === 'all' ? '#374151' : 'white',
                  color: activeCategory === 'all' ? 'white' : '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: 600
                }}
              >
                All
              </button>
              {categories.map(c => (
                <button
                  key={c.id}
                  onClick={() => setActiveCategory(c.id)}
                  style={{
                    padding: '8px 16px', borderRadius: '20px', border: `1px solid ${c.color}`, whiteSpace: 'nowrap',
                    backgroundColor: String(activeCategory) === String(c.id) ? c.color : 'white',
                    color: String(activeCategory) === String(c.id) ? 'white' : c.color, cursor: 'pointer', fontSize: '13px', fontWeight: 600
                  }}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px',
            marginBottom: '80px'
          }}>
            {filteredItems.map(item => (
              <button
                key={item.id}
                onClick={() => openAddModal(item)}
                style={{
                  ...styles.card, cursor: 'pointer', textAlign: 'left', padding: '14px 12px',
                  borderLeft: `5px solid ${item.pos_category_color || '#d1d5db'}`,
                  minHeight: '80px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
                }}
              >
                <div style={{ fontWeight: 600, fontSize: '13px' }}>{item.item_name}</div>
                <div style={{ fontSize: '13px', color: '#4f46e5', fontWeight: 700 }}>
                  {item.pos_price === null ? 'Set price' : formatPeso(item.pos_price)}
                </div>
              </button>
            ))}
            {filteredItems.length === 0 && (
              <div style={{ ...styles.card, gridColumn: '1 / -1', textAlign: 'center', color: '#6b7280' }}>
                No items match.
              </div>
            )}
          </div>

          {/* Bottom cart bar (bottom-sheet summary, mobile-first) — vividly colored
              and elevated once there's something in it, so it can't be missed */}
          <div
            onClick={() => cart.length > 0 && setShowCart(true)}
            style={{
              position: 'fixed', bottom: 0, left: '80px', right: 0,
              backgroundColor: cart.length > 0 ? '#4f46e5' : '#374151', color: 'white',
              padding: cart.length > 0 ? '20px 24px' : '14px 20px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              cursor: cart.length > 0 ? 'pointer' : 'default', zIndex: 40,
              boxShadow: cart.length > 0 ? '0 -6px 20px rgba(79,70,229,0.5)' : 'none',
              borderTop: cart.length > 0 ? '3px solid #818cf8' : 'none',
              transition: 'background-color 0.2s, padding 0.2s, box-shadow 0.2s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ShoppingCart size={cart.length > 0 ? 26 : 20} />
              <span style={{ fontSize: cart.length > 0 ? '17px' : '14px', fontWeight: cart.length > 0 ? 700 : 400 }}>
                {cart.length} item(s)
              </span>
            </div>
            <div style={{ fontSize: cart.length > 0 ? '20px' : '15px', fontWeight: 700 }}>{formatPeso(cartSubtotal)}</div>
          </div>
        </>
      )}

      {/* Add-to-cart modal */}
      <Modal isOpen={!!addingItem} onClose={() => setAddingItem(null)} title={addingItem?.item_name || ''} maxWidth="360px">
        {addingItem && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.label}>{addingItem.sold_by === 'weight' ? 'Weight (kg)' : 'Quantity'}</label>
              <input type="number" step="0.001" value={addQty} onChange={(e) => setAddQty(e.target.value)} style={styles.input} autoFocus />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Price {addingItem.pos_price === null ? '*' : ''}</label>
              <input type="number" step="0.01" value={addPrice} onChange={(e) => setAddPrice(e.target.value)} style={styles.input} />
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setAddingItem(null)} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
              <button onClick={confirmAddToCart} style={styles.button}>Add to Cart</button>
            </div>
          </>
        )}
      </Modal>

      {/* Cart / checkout modal */}
      <Modal isOpen={showCart} onClose={() => setShowCart(false)} title="Cart" maxWidth="480px">
        <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
          {cart.map((line, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>{line.item_name}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{formatPeso(line.unit_price)} each</div>
              </div>
              {line.sold_by === 'weight' ? (
                <span style={{ fontSize: '13px' }}>{line.quantity} kg</span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button onClick={() => updateCartQty(idx, -1)} style={{ ...styles.iconButton, padding: '4px' }}><Minus size={14} /></button>
                  <span style={{ minWidth: '24px', textAlign: 'center' }}>{line.quantity}</span>
                  <button onClick={() => updateCartQty(idx, 1)} style={{ ...styles.iconButton, padding: '4px' }}><Plus size={14} /></button>
                </div>
              )}
              <div style={{ fontWeight: 600, fontSize: '13px', minWidth: '60px', textAlign: 'right' }}>{formatPeso(line.quantity * line.unit_price)}</div>
              <button onClick={() => removeCartLine(idx)} style={{ ...styles.iconButton, color: '#ef4444' }}><Trash2 size={16} /></button>
            </div>
          ))}
          {cart.length === 0 && <div style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>Cart is empty</div>}
        </div>

        <div style={styles.grid2}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Discount</label>
            <select value={saleDiscountId} onChange={(e) => setSaleDiscountId(e.target.value)} style={styles.input}>
              <option value="">None</option>
              {discounts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          {selectedSaleDiscount && selectedSaleDiscount.value === null && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Discount Amount</label>
              <input type="number" step="0.01" value={saleDiscountAmount} onChange={(e) => setSaleDiscountAmount(e.target.value)} style={styles.input} />
            </div>
          )}
        </div>

        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '12px', paddingTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
            <span>Subtotal</span><span>{formatPeso(cartSubtotal)}</span>
          </div>
          {computedSaleDiscount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px', color: '#b91c1c' }}>
              <span>Discount</span><span>-{formatPeso(computedSaleDiscount)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '17px', fontWeight: 700 }}>
            <span>Total</span><span>{formatPeso(grandTotal)}</span>
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label style={styles.label}>Payment Method</label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {paymentMethods.map(m => (
              <button
                key={m.id}
                onClick={() => setPaymentMethod(String(m.id))}
                style={{
                  flex: '1 0 auto', minWidth: '80px', padding: '10px', borderRadius: '6px',
                  border: `1px solid ${m.color}`, cursor: 'pointer',
                  backgroundColor: String(paymentMethod) === String(m.id) ? m.color : 'white',
                  color: String(paymentMethod) === String(m.id) ? 'white' : m.color, fontSize: '13px', fontWeight: 600
                }}
              >
                {m.name}
              </button>
            ))}
          </div>
          {selectedPaymentMethod?.is_cash && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Amount Tendered</label>
              <input type="number" step="0.01" value={amountTendered} onChange={(e) => setAmountTendered(e.target.value)} style={styles.input} />
              {amountTendered !== '' && (
                <div style={{ fontSize: '13px', marginTop: '4px', color: changeAmount < 0 ? '#b91c1c' : '#059669' }}>
                  Change: {formatPeso(changeAmount)}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowCart(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>Continue Shopping</button>
          <button onClick={handleCompleteSale} disabled={submitting || cart.length === 0} style={styles.button}>
            {submitting ? 'Processing...' : 'Complete Sale'}
          </button>
        </div>
      </Modal>

      <PosReceiptView sale={receiptSale} onClose={() => setReceiptSale(null)} />

      <div style={{ marginTop: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>Today's Sales</h3>
        <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Time</th>
                  <th style={styles.th}>Receipt #</th>
                  <th style={styles.th}>Operator</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Status</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {todaySales.map(s => (
                  <tr key={s.id}>
                    <td style={styles.td}>{formatTime(s.created_at)}</td>
                    <td style={styles.td}>{s.receipt_number}</td>
                    <td style={styles.td}>{s.cashier_operator_name}</td>
                    <td style={styles.td}>{formatPeso(s.grand_total)}</td>
                    <td style={styles.td}>
                      <span style={{ color: s.status === 'voided' ? '#b91c1c' : '#059669' }}>{s.status}</span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <button onClick={() => viewReceipt(s.id)} style={{ ...styles.iconButton, color: '#4f46e5', marginRight: '8px' }}>View</button>
                      {s.status !== 'voided' && (
                        <button onClick={() => handleVoid(s.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                          <Ban size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PosSales;
